import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import DepositModal from './DepositModal';
import BuyModal from './BuyModal';
import {
    getUserWallet,
    getUserTransactions,
    transferToUser,
    getEthToUsdRate,
    createWalletIfMissing
} from '../services/walletServiceEnhanced';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabaseClient';
import Loader from './Loader';
import {
    isOnChain,
    isCredit,
    isReversed,
    txLabel as txTitle,
    txIcon,
    timeAgo,
    formatUsd,
    txRowAmountColor,
    txAmountPrefix,
} from '../utils/transactionDisplay';
import './WalletDashboard.css';

const NETWORK_LABEL = (process.env.REACT_APP_ETHEREUM_NETWORK || 'sepolia') === 'mainnet' ? 'Mainnet' : 'Testnet';

const WalletDashboardEnhanced = () => {
    const { user } = useAuth();
    const location = useLocation();

    const [wallet, setWallet] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [ethRate, setEthRate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // UI
    const [copied, setCopied] = useState(false);
    const [txFilter, setTxFilter] = useState('all'); // 'all' | 'onchain' | 'offchain'

    // Modals
    const [showSendModal, setShowSendModal] = useState(false);
    const [showWithdrawPopup, setShowWithdrawPopup] = useState(false);
    const [showReceiveModal, setShowReceiveModal] = useState(false);
    const [showBuyModal, setShowBuyModal] = useState(false);

    // Send form — Sigma Bank users only
    const [sendTo, setSendTo] = useState('');
    const [sendAmount, setSendAmount] = useState('');
    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState('');
    const [sendSuccess, setSendSuccess] = useState('');

    // Deep-link: open send modal when ?action=send
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('action') === 'send') {
            setShowSendModal(true);
        }
    }, [location.search]);

    useEffect(() => {
        if (user?.id) {
            loadWalletData();
            loadEthRate();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const loadWalletData = async () => {
        try {
            setLoading(true);
            setError(null);

            let walletResult = await getUserWallet(user.id);

            if (walletResult.error || !walletResult.data) {
                // Creates the row if it is missing, then asks the server to mint
                // the keypair. The old client-side createUserWallet fallback was
                // removed: keys encrypted in the browser use a different key from
                // the server's and would break the admin sweep path.
                await createWalletIfMissing(user.id);
                walletResult = await getUserWallet(user.id);
            }

            if (walletResult.data) {
                setWallet(walletResult.data);
                // On-chain deposits are detected by the sync-wallets cron, not from the
                // client; the old client-side sync call here was a stub that always
                // reported 0 synced, so its refresh branch could never run.
                loadTransactions(user.id);
                setLoading(false);
                return;
            }
            throw new Error('Unable to initialize wallet. Please refresh the page.');
        } catch (err) {
            console.error('Failed to load wallet:', err);
            setError(err.message || 'Failed to load wallet. Please refresh the page.');
        } finally {
            setLoading(false);
        }
    };

    const loadTransactions = async (userId) => {
        const txResult = await getUserTransactions(userId);
        if (!txResult.error && txResult.data) setTransactions(txResult.data);
    };

    const loadEthRate = async () => {
        const rateResult = await getEthToUsdRate();
        setEthRate(rateResult.data?.rate ?? null);
    };

    const copyAddress = () => {
        if (wallet?.wallet_address) {
            navigator.clipboard.writeText(wallet.wallet_address);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    const closeSendModal = () => {
        if (sending) return;
        setShowSendModal(false);
        setSendError('');
        setSendSuccess('');
        setSendTo('');
        setSendAmount('');
    };

    const handleSendMoney = async (e) => {
        e.preventDefault();
        setSending(true);
        setSendError('');
        setSendSuccess('');

        try {
            const amount = parseFloat(sendAmount);
            if (isNaN(amount) || amount <= 0) {
                setSendError('Please enter a valid amount.');
                setSending(false);
                return;
            }
            if (amount > (wallet.real_balance_usd || wallet.balance_usd || 0)) {
                setSendError('Insufficient spendable balance.');
                setSending(false);
                return;
            }
            if (!sendTo.trim()) {
                setSendError('Please enter the recipient\'s email address.');
                setSending(false);
                return;
            }

            const result = await transferToUser(user.id, sendTo.trim(), amount);
            if (result.error) {
                setSendError(result.error);
            } else {
                setSendSuccess(`$${amount.toFixed(2)} sent successfully!`);
                // Fire-and-forget: notify both sender and receiver. The endpoint
                // requires the sender's own token — it used to accept any
                // fromUserId with no authentication at all.
                const { data: { session: notifySession } } = await supabase.auth.getSession();
                fetch('/api/send-transaction-notification', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${notifySession?.access_token || ''}`,
                    },
                    body: JSON.stringify({
                        fromUserId: user.id,
                        toUserIdentifier: sendTo.trim(),
                        amount: amount,
                        senderName: user.user_metadata?.full_name || user.email,
                    }),
                }).catch(() => {});
                setSendTo('');
                setSendAmount('');
                await loadWalletData();
                setTimeout(() => { setShowSendModal(false); setSendSuccess(''); }, 2000);
            }
        } catch (err) {
            setSendError(err.message);
        } finally {
            setSending(false);
        }
    };

    // A wallet only has a real, controllable address once the server has minted
    // its keypair. Until then we must not show an address or take deposits —
    // funds sent to an unprovisioned address would be unrecoverable.
    const isProvisioned = Boolean(wallet?.provisioned_at && wallet?.wallet_address);

    const filteredTx = transactions.filter(tx => {
        if (txFilter === 'onchain') return isOnChain(tx);
        if (txFilter === 'offchain') return !isOnChain(tx);
        return true;
    });

    if (loading) return <Loader />;

    if (error) {
        return (
            <div style={s.errorWrap}>
                <div style={s.errorIcon}><i className="bx bx-error-circle" style={{ fontSize: 32, color: '#ef4444' }} /></div>
                <p style={s.errorTitle}>Failed to load wallet</p>
                <p style={s.errorMsg}>{error}</p>
                <button onClick={loadWalletData} style={s.retryBtn}>Try Again</button>
            </div>
        );
    }

    if (!wallet) {
        return (
            <div style={s.errorWrap}>
                <Loader />
                <p style={s.errorMsg}>Setting up your wallet…</p>
            </div>
        );
    }

    const spendable = wallet.real_balance_usd || wallet.balance_usd || 0;
    const onChainEth = wallet.balance_eth || 0;
    const onChainUsd = ethRate !== null ? onChainEth * ethRate : null;
    const shortAddress = wallet.wallet_address ? `${wallet.wallet_address.slice(0, 8)}...${wallet.wallet_address.slice(-6)}` : '—';

    return (
        <>
            <div style={s.container}>

                {/* ── Spendable Balance Card ─────────────────────────── */}
                <div style={s.spendableCard}>
                    <div style={s.spendableGlow} aria-hidden="true" />
                    <div style={s.cardTopRow}>
                        <div style={s.cardLabelWrap}>
                            <span style={s.cardLabel}>Spendable Balance</span>
                            <span style={s.cardTag}>Off-Chain · Instant</span>
                        </div>
                        <div style={s.offChipBadge}>
                            <i className="bx bx-transfer" style={{ fontSize: 12 }} /> Off-Chain
                        </div>
                    </div>
                    <div style={s.spendableAmount}>${formatUsd(spendable)}</div>
                    <div style={s.spendableSubtext}>Available for instant transfers</div>
                </div>

                {/* ── Crypto Deposits Card ──────────────────────────── */}
                <div style={s.onChainCard}>
                    <div style={s.onChainGlow} aria-hidden="true" />
                    <div style={s.cardTopRow}>
                        <div style={s.cardLabelWrap}>
                            <span style={s.cardLabel}>Crypto Deposits</span>
                            <span style={s.cardTag}>Blockchain · {NETWORK_LABEL}</span>
                        </div>
                        <div style={s.onChipBadge}>
                            <i className="bx bx-link-alt" style={{ fontSize: 12 }} /> On-Chain
                        </div>
                    </div>
                    <div style={s.onChainUsd}>
                        {onChainUsd !== null
                            ? `$${formatUsd(onChainUsd)}`
                            : <span style={{ color: '#ef4444', fontSize: 12 }}>Error fetching value</span>
                        }
                    </div>
                    <div style={{ ...s.onChainSubtext }}>USD equivalent of crypto deposits</div>
                    {isProvisioned ? (
                        <div style={s.addressRow}>
                            <i className="bx bx-chip" style={s.addressChipIcon} aria-hidden="true" />
                            <span style={s.addressText}>{shortAddress}</span>
                            <button onClick={copyAddress} style={s.copyBtn} aria-label="Copy wallet address">
                                <i className={`bx ${copied ? 'bx-check' : 'bx-copy'}`} />
                                {copied ? ' Copied' : ' Copy'}
                            </button>
                        </div>
                    ) : (
                        <div style={s.addressRow}>
                            <i className="bx bx-loader-alt bx-spin" style={s.addressChipIcon} aria-hidden="true" />
                            <span style={s.addressText}>Your deposit address is being prepared…</span>
                        </div>
                    )}
                </div>

                {/* ── Action Row ────────────────────────────────────── */}
                <div style={s.actionRow}>
                    <button onClick={() => setShowSendModal(true)} style={{ ...s.actionBtn, ...s.sendBtn }}>
                        <div style={s.actionBtnIcon}><i className="bx bx-send" /></div>
                        <span style={s.actionBtnLabel}>Send</span>
                    </button>
                    <button
                        onClick={() => setShowReceiveModal(true)}
                        disabled={!isProvisioned}
                        title={isProvisioned ? undefined : 'Your deposit address is still being prepared'}
                        style={{ ...s.actionBtn, ...s.receiveBtn, ...(isProvisioned ? {} : s.actionBtnDisabled) }}
                    >
                        <div style={s.actionBtnIcon}><i className="bx bx-download" /></div>
                        <span style={s.actionBtnLabel}>Receive</span>
                    </button>
                    <button
                        onClick={() => setShowBuyModal(true)}
                        disabled={!isProvisioned}
                        title={isProvisioned ? undefined : 'Your deposit address is still being prepared'}
                        style={{ ...s.actionBtn, ...s.buyBtn, ...(isProvisioned ? {} : s.actionBtnDisabled) }}
                    >
                        <div style={s.actionBtnIcon}><i className="bx bx-credit-card" /></div>
                        <span style={s.actionBtnLabel}>Buy</span>
                    </button>
                    <button onClick={() => setShowWithdrawPopup(true)} style={{ ...s.actionBtn, ...s.withdrawBtn }}>
                        <div style={s.actionBtnIcon}><i className="bx bx-export" /></div>
                        <span style={s.actionBtnLabel}>Withdraw</span>
                    </button>
                </div>

                {/* ── Transaction History ───────────────────────────── */}
                <div style={s.txSection}>
                    <div style={s.txHeader}>
                        <span style={s.txTitle}>Transaction History</span>
                        {transactions.length > 0 && (
                            <span style={s.txCountBadge}>{transactions.length}</span>
                        )}
                    </div>

                    {/* Filter pills */}
                    <div style={s.filterPills}>
                        {[
                            { key: 'all', label: 'All' },
                            { key: 'onchain', label: 'On-Chain' },
                            { key: 'offchain', label: 'Off-Chain' },
                        ].map(f => (
                            <button
                                key={f.key}
                                style={{ ...s.filterPill, ...(txFilter === f.key ? s.filterPillActive : {}) }}
                                onClick={() => setTxFilter(f.key)}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>

                    {/* Transactions list */}
                    <div style={s.txList}>
                        {filteredTx.length === 0 ? (
                            <div style={s.emptyState}>
                                <div style={s.emptyIcon}>
                                    <i className="bx bx-receipt" style={{ fontSize: 28, color: 'rgba(255,255,255,0.3)' }} />
                                </div>
                                <p style={s.emptyText}>
                                    {txFilter !== 'all' ? `No ${txFilter === 'onchain' ? 'on-chain' : 'off-chain'} transactions` : 'No transactions yet'}
                                </p>
                                <p style={s.emptySubtext}>Your transaction history will appear here</p>
                            </div>
                        ) : (
                            filteredTx.map((tx, idx) => {
                                const incoming = isCredit(tx.transaction_type);
                                const onchain = isOnChain(tx);
                                return (
                                    <div
                                        key={tx.id}
                                        style={{ ...s.txRow, ...(idx < filteredTx.length - 1 ? s.txRowBorder : {}) }}
                                    >
                                        <div style={{
                                            ...s.txIconWrap,
                                            background: incoming ? 'rgba(5,150,105,0.15)' : 'rgba(239,68,68,0.15)',
                                        }}>
                                            <i
                                                className={`bx ${txIcon(tx.transaction_type)}`}
                                                style={{ color: txRowAmountColor(tx, incoming), fontSize: 18 }}
                                            />
                                        </div>
                                        <div style={s.txInfo}>
                                            <div style={s.txTitleRow}>
                                                <span style={s.txTitleText}>{txTitle(tx)}</span>
                                                <span style={{
                                                    ...s.txTypeBadge,
                                                    ...(onchain ? s.txTypeBadgeChain : s.txTypeBadgeOff),
                                                }}>
                                                    {onchain ? 'On-Chain' : 'Off-Chain'}
                                                </span>
                                                {isReversed(tx) && (
                                                    <span style={{ ...s.txTypeBadge, ...s.txTypeBadgeReversed }}>Reversed</span>
                                                )}
                                            </div>
                                            <div style={s.txTime}>{timeAgo(tx.created_at)}</div>
                                        </div>
                                        <div style={{
                                            ...s.txAmount,
                                            color: txRowAmountColor(tx, incoming),
                                            textDecoration: isReversed(tx) ? 'line-through' : 'none',
                                        }}>
                                            {txAmountPrefix(tx, incoming)}${formatUsd(tx.amount_usd)}
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

            </div>

            {/* ── Send Modal ──────────────────────────────────────────── */}
            {showSendModal && (
                <>
                    <div style={s.overlay} onClick={closeSendModal} aria-hidden="true" />
                    <div style={s.modalSheet} role="dialog" aria-label="Send money">
                        <div style={s.modalHandle} aria-hidden="true" />
                        <div style={s.modalHeaderRow}>
                            <div style={s.modalIconWrapSend}>
                                <i className="bx bx-send" style={{ color: '#3b82f6', fontSize: 22 }} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={s.modalTitle}>Send Money</div>
                                <div style={s.modalSubtitle}>Available: ${formatUsd(spendable)}</div>
                            </div>
                            <button style={s.modalCloseBtn} onClick={closeSendModal} disabled={sending} aria-label="Close">
                                <i className="bx bx-x" />
                            </button>
                        </div>

                        {sendSuccess && (
                            <div style={{ ...s.alertBox, ...s.alertSuccess }}>
                                <i className="bx bx-check-circle" style={{ fontSize: 18 }} />
                                <span>{sendSuccess}</span>
                            </div>
                        )}
                        {sendError && (
                            <div style={{ ...s.alertBox, ...s.alertError }}>
                                <i className="bx bx-error-circle" style={{ fontSize: 18 }} />
                                <span>{sendError}</span>
                            </div>
                        )}

                        <form onSubmit={handleSendMoney} style={s.modalForm}>
                            <div style={s.formGroup}>
                                <label style={s.formLabel}>Recipient (Sigma Bank User)</label>
                                <input
                                    type="email"
                                    value={sendTo}
                                    onChange={e => setSendTo(e.target.value)}
                                    placeholder="user@example.com"
                                    style={s.formInput}
                                    required
                                    disabled={sending}
                                />
                                <span style={s.formHint}>Enter the email address of any Sigma Bank user.</span>
                            </div>

                            <div style={s.formGroup}>
                                <label style={s.formLabel}>Amount (USD)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    value={sendAmount}
                                    onChange={e => setSendAmount(e.target.value)}
                                    placeholder="0.00"
                                    style={s.formInput}
                                    required
                                    disabled={sending}
                                />
                                <span style={s.formHint}>Max: ${formatUsd(spendable)}</span>
                            </div>

                            {/* A "Note" field was removed here: process_offchain_transfer takes
                                no note parameter, so whatever the user typed was silently
                                discarded and never displayed anywhere. Restoring it needs an
                                additive migration that replaces the RPC — see qa/BUGS.md BUG-011. */}

                            <button type="submit" style={s.submitBtn} disabled={sending}>
                                {sending ? (
                                    <><i className="bx bx-loader-alt bx-spin" /> Sending…</>
                                ) : (
                                    <><i className="bx bx-send" /> Send Money</>
                                )}
                            </button>
                        </form>
                    </div>
                </>
            )}

            {/* ── Deposit / Receive Modal ──────────────────────────────── */}
            {showReceiveModal && isProvisioned && (
                <DepositModal
                    walletAddress={wallet.wallet_address}
                    onClose={() => setShowReceiveModal(false)}
                />
            )}

            {/* ── Buy Modal (MoonPay) ─────────────────────────────────── */}
            {showBuyModal && isProvisioned && (
                <BuyModal
                    walletAddress={wallet.wallet_address}
                    userId={user?.id}
                    onClose={() => setShowBuyModal(false)}
                    onCompleted={loadWalletData}
                />
            )}

            {/* ── Withdraw Popup ──────────────────────────────────────── */}
            {showWithdrawPopup && (
                <>
                    <div style={s.overlay} onClick={() => setShowWithdrawPopup(false)} aria-hidden="true" />
                    <div style={{ ...s.modalSheet, ...s.withdrawSheet }} role="dialog" aria-label="Withdraw">
                        <div style={s.modalHandle} aria-hidden="true" />
                        <div style={s.withdrawCenter}>
                            <div style={s.withdrawIconWrap}>
                                <i className="bx bx-lock-alt" style={{ color: '#f59e0b', fontSize: 28 }} />
                            </div>
                            <div style={s.withdrawTitle}>Not Available for Your Account</div>
                            <p style={s.withdrawMsg}>
                                This feature is not enabled for you at this time.
                                Please contact support at{' '}
                                <a
                                    href="mailto:support@sigmabank.ai"
                                    style={{ color: '#19bfb1', textDecoration: 'none' }}
                                >
                                    support@sigmabank.ai
                                </a>
                                {' '}for assistance with withdrawals.
                            </p>
                            <button style={s.withdrawCloseBtn} onClick={() => setShowWithdrawPopup(false)}>
                                Got it
                            </button>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

/* ── Styles ──────────────────────────────────────────────────────────────── */
const s = {
    container: {
        padding: '16px 16px 96px',
        maxWidth: 600,
        margin: '0 auto',
    },

    /* Error / Empty states */
    errorWrap: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 24px',
        textAlign: 'center',
        gap: 12,
    },
    errorIcon: { marginBottom: 4 },
    errorTitle: { color: '#fff', fontSize: 17, fontWeight: 700, margin: 0 },
    errorMsg: { color: 'rgba(255,255,255,0.55)', fontSize: 14, margin: 0 },
    retryBtn: {
        marginTop: 8,
        padding: '10px 24px',
        borderRadius: 12,
        border: 'none',
        background: 'rgba(255,255,255,0.1)',
        color: '#fff',
        fontSize: 14,
        fontWeight: 600,
        cursor: 'pointer',
    },

    /* ── Spendable Card ──────────────────────────────────────────── */
    spendableCard: {
        position: 'relative',
        background: 'linear-gradient(145deg, rgba(59,130,246,0.2) 0%, rgba(14,7,88,0.7) 100%)',
        border: '1px solid rgba(59,130,246,0.22)',
        borderRadius: 20,
        padding: '20px 18px 18px',
        marginBottom: 12,
        overflow: 'hidden',
    },
    spendableGlow: {
        position: 'absolute',
        top: -30,
        right: -30,
        width: 110,
        height: 110,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(59,130,246,0.2) 0%, transparent 70%)',
        pointerEvents: 'none',
    },
    spendableAmount: {
        color: '#fff',
        fontSize: 34,
        fontWeight: 800,
        letterSpacing: '-0.8px',
        marginBottom: 4,
    },
    spendableSubtext: {
        color: 'rgba(255,255,255,0.45)',
        fontSize: 12,
    },

    /* ── On-Chain Card ───────────────────────────────────────────── */
    onChainCard: {
        position: 'relative',
        background: 'linear-gradient(145deg, rgba(25,191,177,0.18) 0%, rgba(14,7,88,0.7) 100%)',
        border: '1px solid rgba(25,191,177,0.2)',
        borderRadius: 20,
        padding: '20px 18px 18px',
        marginBottom: 16,
        overflow: 'hidden',
    },
    onChainGlow: {
        position: 'absolute',
        bottom: -30,
        left: -20,
        width: 100,
        height: 100,
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(25,191,177,0.18) 0%, transparent 70%)',
        pointerEvents: 'none',
    },
    onChainEth: {
        color: '#fff',
        fontSize: 28,
        fontWeight: 800,
        letterSpacing: '-0.5px',
        marginBottom: 3,
    },
    ethUnit: {
        fontSize: 18,
        fontWeight: 600,
        color: 'rgba(255,255,255,0.7)',
    },
    onChainUsd: {
        color: '#ffffff',
        fontSize: 26,
        fontWeight: 700,
        letterSpacing: '-0.5px',
        marginBottom: 4,
    },
    onChainSubtext: {
        color: 'rgba(255,255,255,0.45)',
        fontSize: 12,
        marginBottom: 16,
    },
    addressRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        background: 'rgba(0,0,0,0.2)',
        borderRadius: 12,
        padding: '9px 12px',
    },
    addressChipIcon: {
        color: 'rgba(255,255,255,0.35)',
        fontSize: 15,
    },
    addressText: {
        color: 'rgba(255,255,255,0.7)',
        fontSize: 13,
        fontFamily: 'monospace',
        flex: 1,
    },
    copyBtn: {
        background: 'rgba(25,191,177,0.15)',
        border: '1px solid rgba(25,191,177,0.3)',
        color: '#19bfb1',
        borderRadius: 8,
        padding: '4px 10px',
        fontSize: 12,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        whiteSpace: 'nowrap',
        fontWeight: 600,
    },

    /* Shared card top row */
    cardTopRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 12,
    },
    cardLabelWrap: {
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
    },
    cardLabel: {
        color: 'rgba(255,255,255,0.55)',
        fontSize: 12,
        textTransform: 'uppercase',
        letterSpacing: 1,
        fontWeight: 600,
    },
    cardTag: {
        color: 'rgba(255,255,255,0.35)',
        fontSize: 11,
    },
    offChipBadge: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        background: 'rgba(59,130,246,0.15)',
        border: '1px solid rgba(59,130,246,0.3)',
        color: '#3b82f6',
        fontSize: 10,
        fontWeight: 700,
        padding: '4px 9px',
        borderRadius: 20,
        letterSpacing: 0.3,
        textTransform: 'uppercase',
    },
    onChipBadge: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        background: 'rgba(25,191,177,0.12)',
        border: '1px solid rgba(25,191,177,0.25)',
        color: '#19bfb1',
        fontSize: 10,
        fontWeight: 700,
        padding: '4px 9px',
        borderRadius: 20,
        letterSpacing: 0.3,
        textTransform: 'uppercase',
    },

    /* ── Action Row ──────────────────────────────────────────────── */
    actionRow: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr 1fr',
        gap: 10,
        marginBottom: 24,
    },
    actionBtn: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        padding: '16px 8px',
        borderRadius: 16,
        border: 'none',
        cursor: 'pointer',
        transition: 'opacity 0.15s',
    },
    sendBtn: {
        background: 'linear-gradient(135deg, rgba(59,130,246,0.25) 0%, rgba(37,99,235,0.15) 100%)',
        border: '1px solid rgba(59,130,246,0.25)',
    },
    receiveBtn: {
        background: 'linear-gradient(135deg, rgba(25,191,177,0.2) 0%, rgba(14,150,136,0.1) 100%)',
        border: '1px solid rgba(25,191,177,0.2)',
    },
    actionBtnDisabled: {
        opacity: 0.4,
        cursor: 'not-allowed',
    },
    buyBtn: {
        background: 'linear-gradient(135deg, rgba(167,139,250,0.22) 0%, rgba(124,58,237,0.12) 100%)',
        border: '1px solid rgba(167,139,250,0.25)',
    },
    withdrawBtn: {
        background: 'linear-gradient(135deg, rgba(245,158,11,0.18) 0%, rgba(217,119,6,0.1) 100%)',
        border: '1px solid rgba(245,158,11,0.2)',
    },
    actionBtnIcon: {
        width: 40,
        height: 40,
        borderRadius: 12,
        background: 'rgba(255,255,255,0.08)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 20,
        color: '#fff',
    },
    actionBtnLabel: {
        color: 'rgba(255,255,255,0.8)',
        fontSize: 12,
        fontWeight: 600,
    },

    /* ── Transaction Section ─────────────────────────────────────── */
    txSection: {
        borderRadius: 20,
        background: 'rgba(255,255,255,0.025)',
        border: '1px solid rgba(255,255,255,0.06)',
        overflow: 'hidden',
    },
    txHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '16px 16px 12px',
    },
    txTitle: {
        color: '#fff',
        fontSize: 16,
        fontWeight: 700,
        flex: 1,
    },
    txCountBadge: {
        background: 'rgba(255,255,255,0.1)',
        color: 'rgba(255,255,255,0.6)',
        fontSize: 12,
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: 20,
    },
    filterPills: {
        display: 'flex',
        gap: 8,
        padding: '0 16px 14px',
        overflowX: 'auto',
    },
    filterPill: {
        flexShrink: 0,
        padding: '6px 16px',
        borderRadius: 20,
        border: '1px solid rgba(255,255,255,0.12)',
        background: 'transparent',
        color: 'rgba(255,255,255,0.5)',
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.15s',
    },
    filterPillActive: {
        background: 'rgba(25,191,177,0.15)',
        border: '1px solid rgba(25,191,177,0.4)',
        color: '#19bfb1',
    },
    txList: {
        borderTop: '1px solid rgba(255,255,255,0.05)',
    },
    txRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '14px 16px',
    },
    txRowBorder: {
        borderBottom: '1px solid rgba(255,255,255,0.04)',
    },
    txIconWrap: {
        width: 40,
        height: 40,
        borderRadius: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    txInfo: {
        flex: 1,
        minWidth: 0,
    },
    txTitleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        marginBottom: 4,
    },
    txTitleText: {
        color: '#fff',
        fontSize: 14,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        flex: 1,
        minWidth: 0,
    },
    txTypeBadge: {
        flexShrink: 0,
        fontSize: 10,
        fontWeight: 700,
        padding: '2px 7px',
        borderRadius: 20,
        letterSpacing: 0.3,
        textTransform: 'uppercase',
    },
    txTypeBadgeChain: {
        background: 'rgba(25,191,177,0.12)',
        color: '#19bfb1',
        border: '1px solid rgba(25,191,177,0.25)',
    },
    txTypeBadgeOff: {
        background: 'rgba(59,130,246,0.12)',
        color: '#3b82f6',
        border: '1px solid rgba(59,130,246,0.25)',
    },
    txTypeBadgeReversed: {
        background: 'rgba(148,163,184,0.15)',
        color: 'rgba(255,255,255,0.6)',
        border: '1px solid rgba(148,163,184,0.3)',
        marginLeft: 6,
    },
    txTime: {
        color: 'rgba(255,255,255,0.4)',
        fontSize: 12,
    },
    txAmount: {
        fontSize: 15,
        fontWeight: 700,
        whiteSpace: 'nowrap',
    },
    emptyState: {
        textAlign: 'center',
        padding: '36px 20px',
    },
    emptyIcon: {
        width: 56,
        height: 56,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.04)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 12px',
    },
    emptyText: {
        color: 'rgba(255,255,255,0.55)',
        fontSize: 14,
        fontWeight: 500,
        margin: '0 0 4px',
    },
    emptySubtext: {
        color: 'rgba(255,255,255,0.3)',
        fontSize: 12,
        margin: 0,
    },

    /* ── Modals ──────────────────────────────────────────────────── */
    overlay: {
        position: 'fixed',
        inset: 0,
        zIndex: 300,
        background: 'rgba(0,0,0,0.65)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
    },
    modalSheet: {
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 301,
        background: 'linear-gradient(180deg,#1a0e5a 0%,#0e0758 100%)',
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        borderTop: '1px solid rgba(255,255,255,0.1)',
        paddingBottom: 'calc(20px + env(safe-area-inset-bottom,0px))',
        animation: 'sheetSlideUp 0.28s cubic-bezier(0.32,0.72,0,1)',
    },
    modalHandle: {
        width: 40,
        height: 4,
        borderRadius: 2,
        background: 'rgba(255,255,255,0.18)',
        margin: '14px auto 0',
    },
    modalHeaderRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '18px 18px 14px',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
    },
    modalIconWrapSend: {
        width: 44,
        height: 44,
        borderRadius: 12,
        background: 'rgba(59,130,246,0.15)',
        border: '1px solid rgba(59,130,246,0.3)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    modalTitle: {
        color: '#fff',
        fontSize: 17,
        fontWeight: 700,
        marginBottom: 1,
    },
    modalSubtitle: {
        color: 'rgba(255,255,255,0.5)',
        fontSize: 13,
    },
    modalCloseBtn: {
        width: 36,
        height: 36,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.08)',
        border: 'none',
        color: 'rgba(255,255,255,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        fontSize: 22,
        flexShrink: 0,
    },
    alertBox: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        margin: '12px 18px 0',
        padding: '10px 14px',
        borderRadius: 12,
        fontSize: 14,
    },
    alertSuccess: {
        background: 'rgba(5,150,105,0.15)',
        border: '1px solid rgba(5,150,105,0.3)',
        color: '#10b981',
    },
    alertError: {
        background: 'rgba(220,38,38,0.12)',
        border: '1px solid rgba(220,38,38,0.3)',
        color: '#ef4444',
    },
    modalForm: {
        padding: '16px 18px 4px',
    },
    formGroup: {
        marginBottom: 16,
    },
    formLabel: {
        display: 'block',
        color: 'rgba(255,255,255,0.65)',
        fontSize: 13,
        fontWeight: 600,
        marginBottom: 8,
        letterSpacing: 0.3,
    },
    recipientToggle: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 6,
        marginBottom: 10,
    },
    recipientToggleBtn: {
        padding: '8px',
        borderRadius: 10,
        border: '1px solid rgba(255,255,255,0.1)',
        background: 'rgba(255,255,255,0.04)',
        color: 'rgba(255,255,255,0.5)',
        fontSize: 13,
        fontWeight: 500,
        cursor: 'pointer',
    },
    recipientToggleBtnActive: {
        background: 'rgba(59,130,246,0.15)',
        border: '1px solid rgba(59,130,246,0.35)',
        color: '#3b82f6',
        fontWeight: 600,
    },
    formInput: {
        width: '100%',
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 12,
        padding: '12px 14px',
        color: '#fff',
        fontSize: 15,
        outline: 'none',
        boxSizing: 'border-box',
    },
    formTextarea: {
        resize: 'none',
        height: 72,
    },
    formHint: {
        display: 'block',
        color: 'rgba(255,255,255,0.4)',
        fontSize: 12,
        marginTop: 6,
    },
    submitBtn: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: '14px',
        borderRadius: 14,
        border: 'none',
        background: 'linear-gradient(135deg,#3b82f6 0%,#2563eb 100%)',
        color: '#fff',
        fontSize: 15,
        fontWeight: 700,
        cursor: 'pointer',
        marginBottom: 4,
    },

    /* Receive modal */
    receiveLabel: {
        color: 'rgba(255,255,255,0.55)',
        fontSize: 12,
        textTransform: 'uppercase',
        letterSpacing: 1,
        marginBottom: 10,
    },
    receiveAddress: {
        background: 'rgba(0,0,0,0.25)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 12,
        padding: '14px 16px',
        color: 'rgba(255,255,255,0.8)',
        fontSize: 13,
        fontFamily: 'monospace',
        wordBreak: 'break-all',
        lineHeight: 1.6,
        marginBottom: 14,
    },
    receiveCopyBtn: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        padding: '14px',
        borderRadius: 14,
        border: 'none',
        background: 'linear-gradient(135deg,#19bfb1 0%,#0e9688 100%)',
        color: '#fff',
        fontSize: 15,
        fontWeight: 600,
        cursor: 'pointer',
        marginBottom: 14,
    },
    receiveNote: {
        color: 'rgba(255,255,255,0.35)',
        fontSize: 12,
        textAlign: 'center',
        lineHeight: 1.5,
        margin: 0,
    },

    /* Withdraw */
    withdrawSheet: {
        paddingBottom: 'calc(32px + env(safe-area-inset-bottom,0px))',
    },
    withdrawCenter: {
        textAlign: 'center',
        padding: '28px 24px 8px',
    },
    withdrawIconWrap: {
        width: 64,
        height: 64,
        borderRadius: '50%',
        background: 'rgba(245,158,11,0.12)',
        border: '1px solid rgba(245,158,11,0.25)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 16px',
    },
    withdrawTitle: {
        color: '#fff',
        fontSize: 18,
        fontWeight: 700,
        marginBottom: 10,
    },
    withdrawMsg: {
        color: 'rgba(255,255,255,0.55)',
        fontSize: 14,
        lineHeight: 1.5,
        marginBottom: 24,
    },
    withdrawCloseBtn: {
        padding: '12px 40px',
        borderRadius: 14,
        border: '1px solid rgba(255,255,255,0.12)',
        background: 'rgba(255,255,255,0.07)',
        color: '#fff',
        fontSize: 15,
        fontWeight: 600,
        cursor: 'pointer',
    },
};

export default WalletDashboardEnhanced;
